import React, { useState } from "react";
import { Button, Space, Typography, Popconfirm, message, Modal, Input } from "antd";
import { DeleteOutlined, DownloadOutlined, EditOutlined, FileOutlined, FilePdfOutlined, FileExcelOutlined, FileWordOutlined, FileImageOutlined, FileZipOutlined } from "@ant-design/icons";
import { deleteAttachment, getPresignedViewUrl } from "../../../api/attachmentApi";

const { Text, Link } = Typography;

/**
 * 获取附件的 OSS 对象键。
 * 历史上存在两套字段命名：手动上传/内容块生成的附件按 API 规范使用 storage_path，
 * 旧数据和重发种子使用 object_key。删除、去重、下载都必须兼容两者，
 * 否则会出现 DELETE /v1/attachments/undefined 这类错误请求。
 */
export const getAttachmentObjectKey = (attachment) => attachment?.object_key || attachment?.storage_path || null;

/**
 * 根据文件类型返回对应的图标
 */
const getFileIcon = (contentType, filename) => {
    if (contentType.includes("pdf")) {
        return <FilePdfOutlined style={{ color: "#f40" }} />;
    } else if (contentType.includes("excel") || contentType.includes("spreadsheet") || filename.endsWith(".xlsx") || filename.endsWith(".xls")) {
        return <FileExcelOutlined style={{ color: "#52c41a" }} />;
    } else if (contentType.includes("word") || contentType.includes("document") || filename.endsWith(".docx") || filename.endsWith(".doc")) {
        return <FileWordOutlined style={{ color: "#1890ff" }} />;
    } else if (contentType.includes("image")) {
        return <FileImageOutlined style={{ color: "#722ed1" }} />;
    } else if (contentType.includes("zip") || contentType.includes("compressed")) {
        return <FileZipOutlined style={{ color: "#fa8c16" }} />;
    } else {
        return <FileOutlined style={{ color: "#8c8c8c" }} />;
    }
};

/**
 * 格式化文件大小
 */
const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * 附件列表管理组件
 */
const AttachmentList = ({ attachments = [], onAttachmentRemove, onAttachmentRename, onDelete, onRename, allowDelete = true, allowRename = true, allowDownload = true, showFileSize = true }) => {
    const [renameModalVisible, setRenameModalVisible] = useState(false);
    const [renamingAttachment, setRenamingAttachment] = useState(null);
    const [newFileName, setNewFileName] = useState("");

    /**
     * 处理删除附件
     */
    const handleDelete = async (attachment) => {
        try {
            const objectKey = getAttachmentObjectKey(attachment);
            // 重发模式复用的附件（is_reused_from_resend）指向原邮件的 OSS 对象，
            // 物理删除会同时损坏原邮件的附件，这里只从本次邮件的列表中移除。
            // 没有对象键的附件无法定位远端文件，同样只做列表移除。
            if (objectKey && !attachment.is_reused_from_resend) {
                await deleteAttachment(objectKey);
            }
            message.success(`附件 "${attachment.filename}" 删除成功`);

            // 调用新的回调
            if (onAttachmentRemove) {
                onAttachmentRemove(attachment);
            }

            // 向后兼容
            if (onDelete) {
                onDelete(attachment);
            }
        } catch (error) {
            console.error("删除附件失败:", error);
            message.error(`删除附件失败: ${error.message}`);
        }
    };

    /**
     * 开始重命名
     */
    const startRename = (attachment) => {
        setRenamingAttachment(attachment);
        setNewFileName(attachment.filename);
        setRenameModalVisible(true);
    };

    /**
     * 确认重命名
     */
    const confirmRename = () => {
        if (!newFileName.trim()) {
            message.error("文件名不能为空");
            return;
        }

        const oldFileName = renamingAttachment.filename;
        const newName = newFileName.trim();

        // 检查文件名是否有变化
        if (oldFileName === newName) {
            message.info("文件名未发生变化");
            setRenameModalVisible(false);
            setRenamingAttachment(null);
            setNewFileName("");
            return;
        }

        try {
            // 调用新的回调
            if (onAttachmentRename) {
                onAttachmentRename(renamingAttachment, newName);
            }

            // 向后兼容
            if (onRename) {
                onRename(renamingAttachment, newName);
            }

            message.success(`附件已重命名为 "${newName}"`);

            setRenameModalVisible(false);
            setRenamingAttachment(null);
            setNewFileName("");
        } catch (error) {
            console.error("重命名附件失败:", error);
            message.error("重命名失败，请重试");
        }
    };

    /**
     * 处理下载
     */
    const handleDownload = async (attachment) => {
        // 优先使用对象键获取安全的预签名查看链接
        let objectKey = getAttachmentObjectKey(attachment);
        // 如无对象键，尝试从现有下载URL中解析
        if (!objectKey) {
            const rawUrl = attachment.download_url || attachment.url || attachment.attachment_url;
            if (rawUrl) {
                try {
                    const urlObj = new URL(rawUrl);
                    // pathname 以 "/" 开头，去掉并进行解码
                    objectKey = decodeURIComponent(urlObj.pathname.replace(/^\//, ""));
                } catch (e) {
                    // 无法解析URL则忽略
                }
            }
        }

        // 如果能拿到对象键，尝试走预签名查看链接
        if (objectKey) {
            try {
                const { signed_url } = await getPresignedViewUrl(objectKey);
                window.open(signed_url, "_blank");
                return;
            } catch (error) {
                console.warn("获取预签名查看链接失败，回退到直接下载URL:", error);
            }
        }

        // 回退：使用现有的直接下载URL（仅当存在）
        const fallbackUrl = attachment.download_url || attachment.url || attachment.attachment_url;
        if (fallbackUrl) {
            window.open(fallbackUrl, "_blank");
        } else {
            message.warning("该附件暂无下载链接");
        }
    };

    if (!attachments || attachments.length === 0) {
        return <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>暂无附件</div>;
    }

    return (
        <>
            {/* 紧凑的附件显示 */}
            <div className="compact-attachment-list">
                <Space wrap size={[8, 8]}>
                    {attachments.map((attachment, index) => (
                        <div
                            key={getAttachmentObjectKey(attachment) || index}
                            className="compact-attachment-item"
                            style={{
                                display: "inline-flex",
                                flexDirection: "column",
                                padding: "6px 8px",
                                border: "1px solid #d9d9d9",
                                borderRadius: "4px",
                                backgroundColor: "#fafafa",
                                fontSize: "12px",
                                minWidth: "120px",
                                maxWidth: "180px",
                                minHeight: "40px", // 确保有足够的两行高度
                            }}
                        >
                            {/* 第一行：图标和文件名 */}
                            <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
                                {getFileIcon(attachment.content_type || "", attachment.filename || "")}
                                <Text
                                    style={{
                                        fontSize: "12px",
                                        marginLeft: "4px",
                                        flex: 1,
                                        lineHeight: "14px",
                                    }}
                                    ellipsis={{ tooltip: attachment.filename }}
                                >
                                    {attachment.filename}
                                </Text>
                            </div>

                            {/* 第二行：文件大小和操作按钮 */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                {showFileSize && attachment.file_size ? (
                                    <Text type="secondary" style={{ fontSize: "11px", lineHeight: "12px" }}>
                                        {formatFileSize(attachment.file_size)}
                                    </Text>
                                ) : (
                                    <span></span> // 占位符保持布局
                                )}

                                {/* 操作按钮 */}
                                <Space size={2}>
                                    {allowDownload && <Button type="text" size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(attachment)} title="下载" style={{ padding: "0 2px", minWidth: "auto", height: "16px" }} />}
                                    {allowRename && <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startRename(attachment)} title="重命名" style={{ padding: "0 2px", minWidth: "auto", height: "16px" }} />}
                                    {allowDelete && (
                                        <Popconfirm title="确定要删除这个附件吗？" onConfirm={() => handleDelete(attachment)} okText="确定" cancelText="取消">
                                            <Button type="text" size="small" danger icon={<DeleteOutlined />} title="删除" style={{ padding: "0 2px", minWidth: "auto", height: "16px" }} />
                                        </Popconfirm>
                                    )}
                                </Space>
                            </div>
                        </div>
                    ))}
                </Space>
            </div>

            {/* 重命名模态框 */}
            <Modal
                title="重命名附件"
                open={renameModalVisible}
                onOk={confirmRename}
                onCancel={() => {
                    setRenameModalVisible(false);
                    setRenamingAttachment(null);
                    setNewFileName("");
                }}
                okText="确定"
                cancelText="取消"
            >
                <Input value={newFileName} onChange={(e) => setNewFileName(e.target.value)} placeholder="请输入新的文件名" onPressEnter={confirmRename} />
            </Modal>
        </>
    );
};

export default AttachmentList;
